import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export const DepartmentNavigationContext = createContext(null);

export const DepartmentNavigationProvider = ({ activeDept, onNavigate, children }) => {
  const [groups, setGroups] = useState({});
  const [selected, setSelected] = useState(null);
  const [pending, setPending] = useState(null);
  const actions = useRef({});
  const register = useCallback((dept, groupId, label, items, active, action) => {
    const id = `${dept}:${groupId}`;
    actions.current[id] = action;
    setGroups(previous => ({ ...previous, [dept]: { ...previous[dept], [groupId]: { label, items } } }));
    if (active !== null && active !== undefined) setSelected({ dept, groupId, key: active });
    return () => { delete actions.current[id]; };
  }, []);
  const select = useCallback((dept, groupId, key) => {
    const action = actions.current[`${dept}:${groupId}`];
    if (dept === activeDept && action) {
      setSelected({ dept, groupId, key }); action(key);
    } else { setPending({ dept, groupId, key }); onNavigate(dept); }
  }, [activeDept, onNavigate]);
  useEffect(() => {
    if (!pending || pending.dept !== activeDept) return;
    const action = actions.current[`${pending.dept}:${pending.groupId}`];
    if (action) { setPending(null); setSelected(pending); action(pending.key); }
  }, [pending, activeDept, groups]);
  const value = useMemo(() => ({ activeDept, groups, selected, register, select }), [activeDept, groups, selected, register, select]);
  return <DepartmentNavigationContext.Provider value={value}>{children}</DepartmentNavigationContext.Provider>;
};

export function useDepartmentMenu(groupId, label, items, active, onSelect, anchorId) {
  const nav = useContext(DepartmentNavigationContext);
  const actionRef = useRef(onSelect);
  actionRef.current = onSelect;
  const signature = JSON.stringify(items.map(({key, label}) => ({key, label})));
  const register = nav?.register, dept = nav?.activeDept;
  useEffect(() => {
    if (!register || !dept) return;
    return register(dept, groupId, label, items.map(({key, label, icon}) => ({key, label, icon})), active, key => {
      actionRef.current(key);
      if (anchorId) requestAnimationFrame(() => document.querySelector(`[data-testid="${anchorId}"]`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
    });
    // Descriptors are authored arrays; signature prevents object-identity render loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, dept, groupId, label, signature, active, anchorId]);
  return key => nav ? nav.select(dept, groupId, key) : actionRef.current(key);
}

export const DepartmentSectionMenu = ({ sections, prefix }) => {
  const [active, setActive] = useState(sections[0]?.key);
  const choose = useDepartmentMenu('sections', 'Sections', sections, active, key => {
    setActive(key); document.querySelector(`[data-testid="${key}"]`)?.scrollIntoView({behavior:'smooth',block:'start'});
  });
  return <nav className="flex flex-wrap gap-2" aria-label="Department menu tabs" data-testid={`${prefix}-section-menu`}>
    {sections.map(s => <button type="button" key={s.key} onClick={() => choose(s.key)} aria-pressed={s.key === active} data-testid={`${prefix}-section-${s.key}`} className={`rounded-md border px-3 py-2 text-xs font-semibold transition-colors ${active === s.key ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'text-zinc-200 border-zinc-700 hover:border-[#C9A962]'}`}>{s.label}</button>)}
  </nav>;
};